/**
 * 言語に応じた画像の動的読み込みユーティリティ
 */

/**
 * 言語コードから言語識別子を取得
 * @param locale 言語コード（例: 'ja', 'en', 'ja-JP'）
 * @returns 言語識別子（'ja' または 'en'）
 */
export const getLanguageFromLocale = (locale: string): 'ja' | 'en' => {
    return locale.startsWith('ja') ? 'ja' : 'en';
};

// すべての画像を事前に読み込む（import.meta.globは静的文字列リテラルのみ対応）
// 各ディレクトリごとに読み込む必要があるため、必要に応じて追加
const allImageModules = import.meta.glob('@/assets/images/{ja,en}/**/*', {
    eager: true,
    import: 'default'
}) as Record<string, string>;

/**
 * 画像ファイル名から画像パスを取得
 * @param fileName 画像ファイル名（例: 'basicplan-firstview.gif'）
 * @param locale 言語コード
 * @param directoryPath 画像ディレクトリのパス（例: 'plans', 'ads'）
 * @returns 画像パス（見つからない場合は空文字列）
 */
export const getImagePath = (
    fileName: string,
    locale: string,
    directoryPath: string
): string => {
    const lang = getLanguageFromLocale(locale);
    
    // import.meta.globのキーをファイル名で検索
    // キーは /src/assets/images/ja/plans/... または /src/assets/images/en/plans/... の形式
    const moduleKey = Object.keys(allImageModules).find(key => 
        key.includes(`/${lang}/${directoryPath}/`) && 
        (key.endsWith(fileName) || key.includes(`/${fileName}`))
    );
    
    if (moduleKey && allImageModules[moduleKey]) {
        return allImageModules[moduleKey];
    }
    
    return '';
};

/**
 * 画像ファイル名のマッピングから言語に応じた画像パスのオブジェクトを生成
 * @param imageFiles 画像ファイル名のマッピング（例: { basicplanFirstView: 'basicplan-firstview.gif' }）
 * @param locale 言語コード
 * @param directoryPath 画像ディレクトリのパス
 * @returns 画像パスのオブジェクト
 */
export const getLocalizedImages = <T extends Record<string, string>>(
    imageFiles: T,
    locale: string,
    directoryPath: string
): Record<keyof T, string> => {
    const result = {} as Record<keyof T, string>;
    
    for (const [key, fileName] of Object.entries(imageFiles)) {
        result[key as keyof T] = getImagePath(fileName, locale, directoryPath);
    }
    
    return result;
};

